import type { Device } from '@studio/models/device';

/**
 * Client-only calibration attempt-id registry (TD1, ENG-4026).
 *
 * Per knowledge: td1-attempt-id (ai.knowledge/ENG-4026) — we synthesize a
 * client-side UUID when `calibrate_source` fires (start command accepted)
 * and thread it through to the terminal `calibration_completed` event so
 * PostHog report joins are reliable.
 *
 * Eng review: this is a module-level `Map` keyed by deviceId, NOT depot
 * state under `device.control.calibration.clientAttemptId`. Holding it in
 * the depot would pollute proto-derived state and risk leaking attempt
 * identity across reloads. A local Map is trivially removable when the
 * bridge proto carries an attempt id of its own.
 */
const attemptIds = new Map<Device['id'], string>();

/**
 * Synthesize a fresh attempt id. Guards `globalThis.crypto?.randomUUID` and
 * falls back to a time+random id when it is absent — mirrors the sibling
 * `createId` in `analytics/domains/permissions-lifecycle.ts`. The bare
 * `crypto.randomUUID()` global is NOT present in the jest (jsdom/node-VM)
 * test environment, so a guard is required for the unit suite to run.
 */
const createAttemptId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Begin a new calibration attempt for a device: synthesize and store a
 * fresh attempt id, returning it for inclusion in the `calibrate_source`
 * event. Overwrites any prior un-consumed id for the same device.
 */
export const beginCalibrationAttempt = (deviceId: Device['id']): string => {
  const id = createAttemptId();
  attemptIds.set(deviceId, id);
  return id;
};

/**
 * Read AND discard the attempt id for a device. Called by the watchdog at
 * the terminal transition so the id threads start -> completed exactly once
 * and is not leaked into a later attempt.
 */
export const consumeCalibrationAttempt = (deviceId: Device['id']): string | undefined => {
  const id = attemptIds.get(deviceId);
  attemptIds.delete(deviceId);
  return id;
};

/**
 * Read the attempt id for a device WITHOUT discarding it. Use only when a
 * non-terminal read is required; the terminal emit must use
 * {@link consumeCalibrationAttempt} so the id is discarded after the emit.
 */
export const peekCalibrationAttempt = (deviceId: Device['id']): string | undefined =>
  attemptIds.get(deviceId);
